<template>
  <div class="p-2" @click.stop>
    <!-- Edit Property Section -->
    <div v-if="canEditProperty">
      <!-- Formula Type -->
      <FormulaEditDropdown
        v-if="isFormulaType"
        :property-meta="propertyMeta"
        :entity-type="entityType"
        :project-id="projectId"
        :service="service"
        :number-attributes="numberAttributes"
        :is-dynamic-object="isDynamicObject"
        @property-updated="handlePropertyUpdated"
        @close="$emit('close')"
      />

      <!-- Property Sync Type -->
      <PropertySyncEditDropdown
        v-else-if="isPropertySyncType"
        :property-meta="propertyMeta"
        :entity-type="entityType"
        :project-id="projectId"
        :service="service"
        :is-dynamic-object="isDynamicObject"
        @property-updated="handlePropertyUpdated"
        @close="$emit('close')"
      />

      <!-- Record Type -->
      <RecordTypeEditDropdown
        v-else-if="isRecordType"
        :property-meta="propertyMeta"
        :entity-type="entityType"
        :project-id="projectId"
        :service="service"
        :is-dynamic-object="isDynamicObject"
        @property-updated="handlePropertyUpdated"
        @close="$emit('close')"
      />

      <!-- Select Timestamp Type -->
      <SelectTimestampTypeEditDropdown
        v-else-if="isSelectTimestampType"
        :property-meta="propertyMeta"
        :entity-type="entityType"
        :project-id="projectId"
        :service="service"
        :is-dynamic-object="isDynamicObject"
        @property-updated="handlePropertyUpdated"
        @close="$emit('close')"
      />

      <!-- Action Type -->
      <ActionTypeEditDropdown
        v-else-if="isActionType"
        :property-meta="propertyMeta"
        :entity-type="entityType"
        :project-id="projectId"
        :service="service"
        :is-dynamic-object="isDynamicObject"
        @property-updated="handlePropertyUpdated"
        @close="$emit('close')"
      />

      <!-- Default Edit (for select, multi-select, etc.) -->
      <div
        v-else
        class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
        @click.stop="$emit('edit')"
      >
        <ThemeImage
          :icon="imageNames.EDIT"
          alt="edit_icon"
          class="size-5"
        />
        <span class="text-sm text_color">Edit property</span>
      </div>
    </div>

    <!-- Delete Property -->
    <div
      v-if="canDeleteProperty"
      class="flex items-center gap-2 p-2 cursor-pointer dropdown_item"
      @click="$emit('delete')"
    >
      <ThemeImage
        :icon="imageNames.DELETE_GREY"
        alt="delete_icon"
        class="size-5"
      />
      <span class="text-sm text_color">Delete Property</span>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { imageNames } from "@/src/constants/imageConstants.js";
import { ATTRIBUTE_TYPES } from "@/src/constants/crmConstants.js";
import ThemeImage from "@/src/components/ThemeImage.vue";
import FormulaEditDropdown from "./property_edit_dropdowns/FormulaEditDropdown.vue";
import PropertySyncEditDropdown from "./property_edit_dropdowns/PropertySyncEditDropdown.vue";
import RecordTypeEditDropdown from "./property_edit_dropdowns/RecordTypeEditDropdown.vue";
import SelectTimestampTypeEditDropdown from "./property_edit_dropdowns/SelectTimestampTypeEditDropdown.vue";
import ActionTypeEditDropdown from "./property_edit_dropdowns/ActionTypeEditDropdown.vue";

const props = defineProps({
  propertyMeta: {
    type: Object,
    default: null,
  },
  entityType: {
    type: String,
    required: true,
  },
  projectId: {
    type: [String, Number],
    default: null,
  },
  service: {
    type: String,
    default: null,
  },
  isDynamicObject: {
    type: Boolean,
    default: false,
  },
  numberAttributes: {
    type: Array,
    default: () => [],
  },
  canEditProperty: {
    type: Boolean,
    default: false,
  },
  canDeleteProperty: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(["property-updated", "edit", "delete", "close"]);

const isFormulaType = computed(() => {
  return props.propertyMeta?.datatype === ATTRIBUTE_TYPES.FORMULA;
});

const isPropertySyncType = computed(() => {
  return props.propertyMeta?.datatype === ATTRIBUTE_TYPES.PROPERTY_SYNC;
});

const isRecordType = computed(() => {
  return props.propertyMeta?.datatype === ATTRIBUTE_TYPES.RECORD;
});

const isSelectTimestampType = computed(() => {
  return props.propertyMeta?.datatype === ATTRIBUTE_TYPES.SELECT_TIMESTAMP;
});

const isActionType = computed(() => {
  return props.propertyMeta?.datatype === ATTRIBUTE_TYPES.ACTION;
});

const handlePropertyUpdated = (data) => {
  emit("property-updated", data);
};
</script>

